import { useState } from 'react';
import { consentWordingFor, facilityContactConsent, facilityContactConsentRecord, userAlertPhone, userAlertPhoneSet, userAlertPhoneVerify, userContactConsent, type ApiError } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ErrLine, I, btn, sx, useApi, useGuarded } from './kit';
import { consentLine } from './SmsConsentBox';

const INPUT = 'box-sizing:border-box;height:30px;padding:0 9px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FBF8F1;font-size:16px;font-family:inherit;color:#22272E;';

/**
 * A staff member's alert phone, set by their Facility Admin: the number pages and test
 * pages are texted to. It takes over only once confirmed: the server texts a code to the
 * new number (POST /users/:id/phone) and the person reads it back (POST .../verify).
 * Texts and calls need consent (AF-1566): the line under it shows their consent, and the
 * admin can record consent (or its withdrawal) they gave on paper or out loud during
 * onboarding, with the same words the box shows.
 */
export function AlertPhoneEditor({ userId, name }: { userId: string; name: string }) {
  const { client } = useSession();
  const { wr } = useGuarded();
  const phone = useApi((c, s) => userAlertPhone(c, { params: { id: userId }, signal: s }), [userId]);
  const [num, setNum] = useState('');
  const [code, setCode] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);
  const run = async (fn: () => Promise<void>) => {
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  const p = phone.data;
  return (
    <div data-testid="alert-phone-editor" style={sx('margin:0 0 14px;padding-bottom:12px;border-bottom:1px solid rgba(34,39,46,.1);')}>
      <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:6px;')}>Alert phone</div>
      <div style={sx('font-size:16px;margin-bottom:8px;')}>
        {I(p ? (p.phone ? p.phone + (p.verified ? ' · verified' : ' · not verified') : 'None set') + (p.pendingPhone ? ' · code sent to ' + p.pendingPhone : '') : phone.error ? 'Could not load' : '…')}
      </div>
      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:7px;')}>
        <input type="tel" aria-label={`Alert number for ${name}`} placeholder="+15125550100" value={num} onChange={(e) => setNum(e.target.value)} style={sx(INPUT + 'width:150px;')} />
        <a href="#" {...wr(() => void run(async () => {
          const r = await userAlertPhoneSet(client, { params: { id: userId }, body: { phone: num.trim() } });
          setNote(r.live ? 'Code texted. Ask them to read it to you.' : r.reason ?? 'The code was not sent.');
          setNum('');
          phone.reload();
        }))} aria-disabled={!num.trim() || undefined} style={sx(btn(num.trim() ? 'ghost' : 'off'))}>
          Send code
        </a>
        {p?.pendingPhone && (
          <>
            <input type="text" inputMode="numeric" aria-label={`Code ${name} received`} placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))} style={sx(INPUT + 'width:110px;')} />
            <a href="#" {...wr(() => void run(async () => {
              await userAlertPhoneVerify(client, { params: { id: userId }, body: { code } });
              setCode('');
              setNote(null);
              phone.reload();
            }))} aria-disabled={code.length !== 6 || undefined} style={sx(btn(code.length === 6 ? 'primary' : 'off'))}>
              Verify
            </a>
          </>
        )}
      </div>
      {p && (p.phone || p.pendingPhone) && (
        <div data-testid="sms-consent-admin" style={sx('margin-top:8px;')}>
          <div style={sx('font-size:16px;color:#54606C;margin-bottom:5px;text-wrap:pretty;')}>{I(consentLine(p.consent))}</div>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:7px;')}>
            <span style={sx('font-size:16px;color:#3C454F;')}>{I(p.consent?.onFile ? 'They withdrew it:' : 'They agreed during onboarding:')}</span>
            {(['paper', 'verbal'] as const).map((src) => (
              <a key={src} href="#" {...wr(() => void run(async () => {
                await userContactConsent(client, { params: { id: userId }, body: { granted: !p.consent?.onFile, source: src, wording: consentWordingFor('CAREGIVER') } });
                setNote(null);
                phone.reload();
              }))} style={sx(btn('ghost'))}>
                {I((p.consent?.onFile ? 'Record withdrawal (' : 'Record consent (') + (src === 'paper' ? 'paper form' : 'verbal') + ')')}
              </a>
            ))}
          </div>
          <div style={sx('font-size:16px;color:#54606C;margin-top:5px;text-wrap:pretty;')}>
            {I('Only they can tick the box themselves (their own settings or the app). Recording here names you and the time.')}
          </div>
        </div>
      )}
      {note && <div role="status" style={sx('font-size:16px;color:#54606C;margin-top:6px;')}>{I(note)}</div>}
      <ErrLine error={err} />
    </div>
  );
}

/**
 * The building's contact number (the Pulse downtime fallback texts it) and its text and
 * call consent. The number itself is set on the building's Pulse profile; here the
 * building's Facility Admin (or Aithora) records consent given for it on paper or out loud,
 * with the same words and buttons as a staff member's alert phone. The server records it
 * for the number on file, never one typed in. Without it the fallback text is skipped.
 */
export function BuildingContactConsent({ orgId }: { orgId: string | null }) {
  const { client } = useSession();
  const { wr } = useGuarded();
  const view = useApi((c, s) => facilityContactConsent(c, { params: { id: orgId ?? 'me' }, signal: s }), [orgId], !!orgId);
  const [err, setErr] = useState<ApiError | null>(null);
  const v = view.data;
  if (!orgId || !v) return null;
  const record = (src: 'paper' | 'verbal') => wr(() => void (async () => {
    setErr(null);
    try {
      await facilityContactConsentRecord(client, { params: { id: orgId }, body: { granted: !v.consent.onFile, source: src, wording: v.wording } });
      view.reload();
    } catch (e) {
      setErr(e as ApiError);
    }
  })());
  return (
    <div data-testid="building-contact-consent" style={sx('margin:0 0 14px;padding-top:12px;border-top:1px solid rgba(34,39,46,.1);')}>
      <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:6px;')}>Building contact number</div>
      <div style={sx('font-size:16px;margin-bottom:6px;')}>
        {I(v.phone ? v.phone + ' · gets the downtime text when Aithora is down' : 'None on file. Aithora sets it on the building’s profile.')}
      </div>
      {v.phone && (
        <>
          <div style={sx('font-size:16px;color:#54606C;margin-bottom:5px;text-wrap:pretty;')}>
            {I(v.optedOut ? 'This number replied STOP, so no texts or calls go to it. Only someone at that number can text START.' : consentLine(v.consent))}
          </div>
          {!v.optedOut && (
            <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:7px;')}>
              <span style={sx('font-size:16px;color:#3C454F;')}>{I(v.consent.onFile ? 'They withdrew it:' : 'They agreed:')}</span>
              {(['paper', 'verbal'] as const).map((src) => (
                <a key={src} href="#" {...record(src)} style={sx(btn('ghost'))}>
                  {I((v.consent.onFile ? 'Record withdrawal (' : 'Record consent (') + (src === 'paper' ? 'paper form' : 'verbal') + ')')}
                </a>
              ))}
            </div>
          )}
          <div style={sx('font-size:16px;color:#54606C;margin-top:5px;text-wrap:pretty;')}>
            {I('The words they agree to: “' + v.wording + '” Recording here names you and the time.')}
          </div>
        </>
      )}
      <ErrLine error={err} />
    </div>
  );
}
